import { useEffect, useRef, useCallback, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { postLocation, clearUserLocation } from "../api/locationAPI";
import { updateCurrentUser } from "../redux/actions/userActions";
import { getUserSettings, saveUserSettings } from "../api/usersAPI";
import { SAVE_USER_SETTINGS } from "../redux/actions/actionTypes";
import { fetchLocation } from "../redux/reducers/locationSlice";
import { getIPLocation } from "../utils/fallbackLocation";
import { clearLocation } from "../redux/reducers/locationSlice";

const useLocationUpdate = () => {
  const dispatch = useDispatch();

  const loggedUser = useSelector((state) => state.loggedUser, shallowEqual);
  const locationEnabledInApp = useSelector(
    (state) => state.userSettings?.userSettings?.locationSharingEnabled,
    shallowEqual,
  );

  const [localLocationEnabled, setLocalLocationEnabled] = useState(false); // ⚠️ Always start as false - only enable on user gesture
  const [locationPermission, setLocationPermission] = useState("prompt");
  const [isIncognito, setIsIncognito] = useState(false);
  const [isEnablingLocation, setIsEnablingLocation] = useState(false);
  const [isDisablingLocation, setIsDisablingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);

  const userIdRef = useRef(loggedUser?.user?.id);
  const localLocationEnabledRef = useRef(false); // ⚠️ Always false initially
  const lastLocation = useRef({ lat: null, lon: null });
  const lastUpdateTime = useRef(0);
  const watchIdRef = useRef(null);
  const retryCountRef = useRef(0);
  const errorTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  const maxRetries = 3;
  const updateInterval = 10 * 60 * 1000; // 10 minutes (Time buffer)
  const locationChangeThreshold = 500; // 500 meters (Distance buffer)

  // Track mount status
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Sync state with Redux on mount - restore enabled state if user previously allowed it
  useEffect(() => {
    if (locationEnabledInApp && locationPermission === "granted") {
      // User has previously enabled location and browser has permission
      // Restore the enabled state without prompting again
      if (!localLocationEnabledRef.current && isMountedRef.current) {
        setLocalLocationEnabled(true);
        localLocationEnabledRef.current = true;
      }
    }
  }, [locationEnabledInApp, locationPermission]);

  useEffect(() => {
    userIdRef.current = loggedUser?.user?.id;
  }, [loggedUser?.user?.id]);

  const haversineDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const R = 6371000;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }, []);

  const saveLocation = useCallback(
    async (latitude, longitude) => {
      if (!localLocationEnabledRef.current || !userIdRef.current) return;

      const now = Date.now();
      const isFirstUpdate = lastLocation.current.lat === null;

      const distMoved = haversineDistance(
        lastLocation.current.lat,
        lastLocation.current.lon,
        latitude,
        longitude,
      );

      const hasMovedSignificantly = distMoved > locationChangeThreshold;
      const hasTimeBufferPassed =
        now - lastUpdateTime.current >= updateInterval;

      // STRICT LOGIC:
      // 1. Always allow the very first update.
      // 2. Subsequent updates REQUIRE significant movement AND the time buffer to be met.
      if (!isFirstUpdate && (!hasMovedSignificantly || !hasTimeBufferPassed)) {
        return;
      }

      try {
        if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);

        // Check localStorage cache first
        const cacheKey = `location_${Math.round(latitude * 100)}_${Math.round(longitude * 100)}`;
        const cachedData = localStorage.getItem(cacheKey);
        let locationData;

        if (cachedData) {
          const parsed = JSON.parse(cachedData);
          // Use cache if less than 24 hours old
          if (now - parsed.timestamp < 24 * 60 * 60 * 1000) {
            locationData = parsed.data;
          }
        }

        // Fetch from API if no valid cache
        if (!locationData) {
          const resultAction = await dispatch(
            fetchLocation({ lat: latitude, lon: longitude }),
          );

          if (!fetchLocation.fulfilled.match(resultAction)) {
            throw new Error("Failed to fetch reverse-geocoding data");
          }

          locationData = resultAction.payload;

          // Cache in localStorage
          localStorage.setItem(
            cacheKey,
            JSON.stringify({
              data: locationData,
              timestamp: now,
            }),
          );
        }

        const resultPostLocation = await postLocation(
          userIdRef.current,
          locationData.city,
          locationData.country,
          latitude,
          longitude,
        );

        // Only update refs if the network request succeeded
        lastUpdateTime.current = now;
        lastLocation.current = { lat: latitude, lon: longitude };

        // Update only location fields in Redux, not the entire user object
        dispatch(updateCurrentUser(resultPostLocation.user));
        retryCountRef.current = 0;
      } catch (error) {
        console.error("Location sync failed:", error);

        // Only retry on network errors, not rate limiting (429)
        const is429Error = error.response?.status === 429;

        if (is429Error) {
          // For 429 errors, rely on API client's built-in retry logic with exponential backoff
          // Don't retry here to avoid cascading retry storms
          console.warn(
            "Rate limited. API client will handle retry with exponential backoff.",
          );
          retryCountRef.current = 0;
          return;
        }

        // Retry logic only for genuine network failures
        retryCountRef.current += 1;
        if (retryCountRef.current <= maxRetries) {
          const delay = Math.min(1000 * 2 ** retryCountRef.current, 30000);
          errorTimeoutRef.current = setTimeout(() => {
            if (localLocationEnabledRef.current) {
              saveLocation(latitude, longitude);
            }
          }, delay);
        } else {
          retryCountRef.current = 0;
        }
      }
    },
    [dispatch, haversineDistance, updateInterval, locationChangeThreshold],
  );

  const attemptGeolocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation unsupported"));
        return;
      }

      const timeout = setTimeout(() => reject(new Error("Timeout")), 10000);

      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          clearTimeout(timeout);
          resolve({ latitude: coords.latitude, longitude: coords.longitude });
        },
        (err) => {
          clearTimeout(timeout);
          reject(err);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000,
        },
      );
    });
  }, []);

  const startWatcher = useCallback(() => {
    if (!localLocationEnabledRef.current || watchIdRef.current !== null) return;

    // Only start watching if geolocation is available
    if (!navigator.geolocation) return;

    watchIdRef.current = navigator.geolocation.watchPosition(
      ({ coords }) => {
        saveLocation(coords.latitude, coords.longitude);
      },
      async () => {
        try {
          const fallback = await getIPLocation();
          if (fallback) {
            saveLocation(fallback.latitude, fallback.longitude);
          }
        } catch {}
      },
      {
        enableHighAccuracy: false,
        maximumAge: 300000,
        timeout: 15000,
      },
    );
  }, [saveLocation]);

  const stopWatcher = useCallback(() => {
    if (watchIdRef.current !== null) {
      if (navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      watchIdRef.current = null;
    }
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
  }, []);

  // Detect incognito mode
  useEffect(() => {
    const checkIncognito = async () => {
      try {
        if (navigator.storage?.estimate) {
          const { quota } = await navigator.storage.estimate();
          if (isMountedRef.current) setIsIncognito(quota < 120000000);
        } else {
          const fs = window.RequestFileSystem || window.webkitRequestFileSystem;
          if (fs) {
            await new Promise((resolve) =>
              fs(
                window.TEMPORARY,
                100,
                () => resolve(false),
                () => resolve(true),
              ),
            ).then((isPrivate) => {
              if (isMountedRef.current) setIsIncognito(isPrivate);
            });
          } else if (isMountedRef.current) {
            setIsIncognito(false);
          }
        }
      } catch {
        if (isMountedRef.current) setIsIncognito(true);
      }
    };
    checkIncognito();
  }, []); // Fetch user settings on user change
  const hasRequestedSettings = useRef(false);
  useEffect(() => {
    if (userIdRef.current && !hasRequestedSettings.current) {
      hasRequestedSettings.current = true;
      getUserSettings(userIdRef.current)
        .then((settings) =>
          dispatch({ type: SAVE_USER_SETTINGS, payload: settings }),
        )
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedUser?.user?.id]);

  // Permission listener
  useEffect(() => {
    let perm;
    if (!navigator.permissions?.query) {
      setLocationPermission("unsupported");
      return;
    }

    navigator.permissions
      .query({ name: "geolocation" })
      .then((permissionStatus) => {
        perm = permissionStatus;
        setLocationPermission(perm.state);
        perm.onchange = () => {
          setLocationPermission(perm.state);
          if (perm.state === "denied") stopWatcher();
        };
      });

    return () => {
      if (perm) perm.onchange = null;
    };
  }, [stopWatcher]);

  // Start/stop watcher based on state
  useEffect(() => {
    if (
      localLocationEnabledRef.current &&
      locationPermission === "granted" &&
      !isIncognito
    ) {
      startWatcher();
    } else {
      stopWatcher();
    }
  }, [locationPermission, isIncognito, startWatcher, stopWatcher]);

  const enableLocationInApp = async () => {
    if (!userIdRef.current || isEnablingLocation) return false;

    setIsEnablingLocation(true);
    setLocationError(null);

    // Create abort controller for this operation
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      // Check if component is still mounted
      if (!isMountedRef.current) return false;

      const coords = await attemptGeolocation();
      if (signal.aborted || !isMountedRef.current) return false;

      const locationData = await dispatch(
        fetchLocation({ lat: coords.latitude, lon: coords.longitude }),
      ).unwrap();

      if (signal.aborted || !isMountedRef.current) return false;

      const result = await postLocation(
        userIdRef.current,
        locationData.city,
        locationData.country,
        coords.latitude,
        coords.longitude,
      );

      if (signal.aborted || !isMountedRef.current) return false;

      dispatch(updateCurrentUser(result.user));

      if (!localLocationEnabledRef.current) {
        const updatedSettings = await saveUserSettings(userIdRef.current, {
          locationSharingEnabled: true,
        });
        dispatch({
          type: SAVE_USER_SETTINGS,
          payload: { userSettings: updatedSettings.settings },
        });
      }

      if (isMountedRef.current) {
        setLocalLocationEnabled(true);
        localLocationEnabledRef.current = true;
        setLocationPermission("granted");
        setIsEnablingLocation(false);
      }
      return true;
    } catch (error) {
      if (signal.aborted || !isMountedRef.current) return false;

      console.error("Location enable failed", error);

      let errorMessage =
        "An error occurred while enabling location. Please try again.";
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);

      if (error.code === 1 || error.message?.includes("denied")) {
        errorMessage = "Location access is blocked.";
        if (isIOS) {
          errorMessage =
            "To enable location on iOS:\n1. Open System Settings\n2. Go to Privacy > Location Services\n3. Ensure it's ON and Safari is set to 'While Using'.";
        } else if (isAndroid) {
          errorMessage =
            "To enable location on Android:\n1. Tap the three dots (⋮) or Lock icon in Chrome\n2. Go to 'Site settings'\n3. Tap 'Location' and select 'Allow'.";
        } else {
          errorMessage =
            "Location access is blocked by your browser.\n\nClick the Lock icon (🔒) in your address bar and set 'Location' to 'Allow'.";
        }
      } else if (error.message === "Timeout") {
        errorMessage =
          "Location request timed out. Please check your signal and try again.";
      }

      if (isMountedRef.current) {
        setLocationError(errorMessage);
        setIsEnablingLocation(false);
      }

      // For user-facing error, show alert
      alert(errorMessage);
      return false;
    }
  };

  const disableLocationInApp = useCallback(async () => {
    // Always update local state immediately to respect user's choice
    setIsDisablingLocation(true);
    setLocationError(null);
    stopWatcher();
    setLocalLocationEnabled(false);
    localLocationEnabledRef.current = false;

    // Clear local location refs
    lastLocation.current = { lat: null, lon: null };
    lastUpdateTime.current = 0;

    // Update Redux immediately so UI reflects the choice
    dispatch({
      type: SAVE_USER_SETTINGS,
      payload: { userSettings: { locationSharingEnabled: false } },
    });

    // Clear location from Redux state
    dispatch(clearLocation());
    dispatch(
      updateCurrentUser({
        city: null,
        country: null,
        latitude: null,
        longitude: null,
      }),
    );

    // If we don't have a user ID yet (new user), skip the API call
    // The setting is already in Redux and will be persisted when user ID becomes available
    if (!userIdRef.current) {
      setIsDisablingLocation(false);
      return;
    }

    try {
      // Save the setting AND clear coordinates from the database
      const [updatedSettings] = await Promise.all([
        saveUserSettings(userIdRef.current, {
          locationSharingEnabled: false,
        }),
        clearUserLocation(userIdRef.current),
      ]);

      if (isMountedRef.current) {
        dispatch({
          type: SAVE_USER_SETTINGS,
          payload: { userSettings: updatedSettings.settings },
        });
        setIsDisablingLocation(false);
      }
    } catch (error) {
      console.error("Failed to disable location", error);
      if (isMountedRef.current) {
        // Don't revert local state - user said no, respect that
        setLocationError("Failed to save location preference.");
        setIsDisablingLocation(false);
      }
    }
  }, [stopWatcher, dispatch]);

  return {
    locationPermission,
    isIncognito,
    enableLocationInApp,
    disableLocationInApp,
    // Return Redux state directly - null means "not decided yet"
    // true = enabled, false = denied, null = show prompt
    locationEnabledInApp,
    isEnablingLocation,
    isDisablingLocation,
    locationError,
  };
};

export default useLocationUpdate;
